import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  getTeamMemberPaymentById,
  updateTeamMemberPayment,
  type UpdateTeamMemberPaymentPayload,
} from "../../../../Redux/ApiCalls/Dashboard/TeamMemberPaymentApi";
import {
  X,
  Sparkles,
  Wallet,
  IndianRupee,
  CheckCircle2,
  Loader2,
  Receipt,
} from "lucide-react";

interface UpdatePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentId: number | string | null;
  onSuccess?: () => void;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

export default function UpdatePaymentModal({
  isOpen,
  onClose,
  paymentId,
  onSuccess,
}: UpdatePaymentModalProps) {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");
  const [paymentSnapshot, setPaymentSnapshot] = useState<{
    totalAmount: number;
    paidSoFar: number;
    description: string;
    notes: string;
  } | null>(null);
  const [pendingAmount, setPendingAmount] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!isOpen || paymentId == null) return;
    setFetching(true);
    setError("");
    setPendingAmount("");
    getTeamMemberPaymentById(paymentId)
      .then((payment) => {
        if (payment) {
          const total = Number(payment.amount) || 0;
          const paid = Number(payment.paid_amount) || 0;
          setPaymentSnapshot({
            totalAmount: total,
            paidSoFar: paid,
            description: payment.description ?? "",
            notes: payment.notes ?? "",
          });
          setDescription(payment.description ?? "");
          setNotes(payment.notes ?? "");
        } else {
          setError("Failed to load payment.");
        }
      })
      .finally(() => setFetching(false));
  }, [isOpen, paymentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentId == null || !paymentSnapshot) return;
    const pending = pendingAmount.trim() ? Number(pendingAmount) : 0;
    if (pending <= 0) {
      setError("Please enter the pending amount you are paying now.");
      return;
    }
    const newPaidTotal = paymentSnapshot.paidSoFar + pending;
    if (newPaidTotal > paymentSnapshot.totalAmount) {
      setError(
        `Pending amount cannot exceed remaining ₹${(paymentSnapshot.totalAmount - paymentSnapshot.paidSoFar).toLocaleString("en-IN", { minimumFractionDigits: 2 })}.`
      );
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload: UpdateTeamMemberPaymentPayload = {
        amount: String(paymentSnapshot.totalAmount),
        paid_amount: String(newPaidTotal),
        description: description || undefined,
        notes: notes || undefined,
      };
      const result = await updateTeamMemberPayment(paymentId, payload);
      if (result != null) {
        onSuccess?.();
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const formatRs = (n: number) =>
    `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

  const currentRemaining = paymentSnapshot
    ? paymentSnapshot.totalAmount - paymentSnapshot.paidSoFar
    : 0;
  const pendingNum = pendingAmount.trim() ? Number(pendingAmount) : 0;
  const newRemaining = paymentSnapshot
    ? Math.max(0, paymentSnapshot.totalAmount - paymentSnapshot.paidSoFar - pendingNum)
    : 0;

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroTop>
            <HeroIcon>
              <Wallet size={22} />
            </HeroIcon>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                Team payout
              </HeroBadge>
              <ModalTitle>Update payment</ModalTitle>
              <ModalSubtitle>
                Record a partial or full payment against this entry.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroTop>
        </ModalHero>

        <ModalBody>
          {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

          {fetching ? (
            <LoadingWrap>
              <Spinner size={28} color="#c94daa" />
              Loading payment…
            </LoadingWrap>
          ) : (
            <form onSubmit={handleSubmit}>
              {paymentSnapshot && (
                <>
                  <SectionLabel>
                    <Receipt size={12} />
                    Payment summary
                  </SectionLabel>
                  <SummaryGrid>
                    <SummaryItem>
                      <SummaryLabel>Total amount</SummaryLabel>
                      <SummaryValue>{formatRs(paymentSnapshot.totalAmount)}</SummaryValue>
                    </SummaryItem>
                    <SummaryItem>
                      <SummaryLabel>Paid so far</SummaryLabel>
                      <SummaryValue $tone="#31a66a">
                        {formatRs(paymentSnapshot.paidSoFar)}
                      </SummaryValue>
                    </SummaryItem>
                    <SummaryItem $wide>
                      <SummaryLabel>Current remaining</SummaryLabel>
                      <SummaryValue $tone="#e44862">
                        {formatRs(currentRemaining)}
                      </SummaryValue>
                    </SummaryItem>
                  </SummaryGrid>
                </>
              )}

              <SectionLabel>Payment update</SectionLabel>
              <FieldGrid>
                <FieldFull>
                  <Label>
                    Pending amount (paying now)<Required>*</Required>
                  </Label>
                  <InputWrap>
                    <IndianRupee size={16} color="#c94daa" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={pendingAmount}
                      onChange={(e) => {
                        setError("");
                        setPendingAmount(e.target.value);
                      }}
                      placeholder="Enter amount you are paying now"
                    />
                  </InputWrap>
                  {pendingAmount.trim() && paymentSnapshot && (
                    <HintText>
                      After this payment, remaining will be:{" "}
                      <strong>{formatRs(newRemaining)}</strong>
                    </HintText>
                  )}
                </FieldFull>

                <FieldFull>
                  <Label>Description</Label>
                  <InputWrap>
                    <Input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Payment description"
                    />
                  </InputWrap>
                </FieldFull>

                <FieldFull>
                  <Label>Notes</Label>
                  <TextArea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes"
                  />
                </FieldFull>
              </FieldGrid>

              <ActionRow>
                <CancelBtn type="button" onClick={onClose}>
                  Cancel
                </CancelBtn>
                <SubmitBtn type="submit" disabled={loading}>
                  {loading ? (
                    "Saving..."
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Update payment
                    </>
                  )}
                </SubmitBtn>
              </ActionRow>
            </form>
          )}
        </ModalBody>
      </ModalBox>
    </Overlay>
  );
}

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 9999;
  padding: 16px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.1) 55%,
    rgba(53, 183, 200, 0.06) 100%
  );
  border-bottom: 1px solid rgba(201, 77, 170, 0.1);
  flex-shrink: 0;
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.25) 0%, transparent 70%);
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.25);
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const ErrorBanner = styled.p`
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(228, 72, 98, 0.08);
  border: 1px solid rgba(228, 72, 98, 0.2);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #e44862;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 160px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 16px 0 10px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  &:first-of-type {
    margin-top: 0;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 4px;
`;

const SummaryItem = styled.div<{ $wide?: boolean }>`
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  ${(p) => (p.$wide ? "grid-column: 1 / -1;" : "")}
`;

const SummaryLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div<{ $tone?: string }>`
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: ${(p) => p.$tone ?? "#171717"};
`;

const FieldGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldFull = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
`;

const Required = styled.span`
  color: #e44862;
  margin-left: 2px;
`;

const InputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus-within {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #171717;
  resize: vertical;
  min-height: 72px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
  }
`;

const HintText = styled.p`
  margin: 6px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
  strong {
    font-weight: 700;
    color: #171717;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`;

const CancelBtn = styled.button`
  flex: 0 0 auto;
  padding: 0 20px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`;

const SubmitBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;
